import { randomUUID } from 'crypto';
import z from 'zod';
import type { Agent, AgentOutputType } from '../../agent';
import { UserError } from '../../errors';
import logger from '../../logger';
import type { RunState } from '../../run-state';
import type { AgentInputItem } from '../../types';
import type {
  Memory,
  MemoryFlushMode,
  MemoryGenerateConfig,
} from '../capabilities/memory';
import { Manifest } from '../manifest';
import { registerSandboxPreStopHook } from '../runtime/session-lifecycle';
import { withSandboxSpan } from '../runtime/spans';
import type { SandboxSessionLike, SandboxSessionState } from '../session';
import { stableJsonStringify } from '../shared/stable-json';
import {
  renderMemoryConsolidationPrompt,
  renderRolloutExtractionInstructions,
  renderRolloutExtractionUserPrompt,
} from './prompts';
import {
  buildMemoryRolloutPayload,
  type MemoryRolloutIdentity,
  normalizeRolloutSlug,
  renderPhaseOnePrompt,
  resolveMemoryRolloutId,
  validateRolloutId,
} from './rollouts';
import {
  extractMemoryMetadataValue,
  joinRelativePaths,
  SandboxMemoryStorage,
} from './storage';

const objectIds = new WeakMap<object, number>();
let nextObjectId = 1;

const RolloutExtractionArtifactsSchema = z.object({
  rollout_summary: z.string(),
  rollout_slug: z.string(),
  raw_memory: z.string(),
});

type RolloutExtractionArtifacts = z.infer<
  typeof RolloutExtractionArtifactsSchema
>;

type MemoryLayoutKey = string;

const managersBySession = new WeakMap<
  SandboxSessionLike<SandboxSessionState>,
  Map<MemoryLayoutKey, MemoryManagerRegistryEntry>
>();

type MemoryManagerRegistryEntry = {
  memory: Memory;
  manager: SandboxMemoryGenerationManager;
  generationSignature: string;
  layout: {
    memoriesDir: string;
    sessionsDir: string;
    summaryFile: string;
  };
};

export type SandboxMemoryAgentRunner = (
  agent: Agent<any, any>,
  input: string,
  options: {
    sandbox: {
      session: SandboxSessionLike<SandboxSessionState>;
      manifest?: Manifest;
    };
    maxTurns?: number;
  },
) => Promise<{ finalOutput: unknown; output: unknown }>;

export function getOrCreateSandboxMemoryGenerationManager(args: {
  session: SandboxSessionLike<SandboxSessionState>;
  memory: Memory;
  runAs?: string;
  runAgent: SandboxMemoryAgentRunner;
}): SandboxMemoryGenerationManager {
  if (args.memory.generate === null) {
    throw new UserError(
      'SandboxMemoryGenerationManager requires Memory.generate to be enabled.',
    );
  }

  // 这里是 memory 生成的命名空间：同一个 SandboxSession 下，相同 layout
  // 复用同一个 manager；不同 memoriesDir/sessionsDir 则被视为隔离的 memory。
  const layout = normalizedLayout(args.memory);
  const layoutKey = memoryLayoutKey(args.memory);
  let registry = managersBySession.get(args.session);
  const existing = registry?.get(layoutKey);
  if (existing) {
    const nextSignature = memoryGenerationSignature(args.memory.generate);
    if (existing.generationSignature !== nextSignature) {
      throw new UserError(
        'Sandbox session already has a different Memory generation config attached for this memory layout.',
      );
    }
    if (existing.manager.isStopped()) {
      registry!.delete(layoutKey);
      if (registry!.size === 0) {
        managersBySession.delete(args.session);
        registry = undefined;
      }
    } else {
      existing.manager.updateRuntimeContext({
        runAs: args.runAs,
        runAgent: args.runAgent,
      });
      return existing.manager;
    }
  }

  if (registry) {
    for (const entry of registry.values()) {
      if (entry.layout.memoriesDir === layout.memoriesDir) {
        throw new UserError(
          `Sandbox session already has a Memory generation capability for memoriesDir=${layout.memoriesDir}. Use a different memoriesDir for isolated memories, or the same layout to share memory.`,
        );
      }
      if (entry.layout.sessionsDir === layout.sessionsDir) {
        throw new UserError(
          `Sandbox session already has a Memory generation capability for sessionsDir=${layout.sessionsDir}. Use a different sessionsDir for isolated memories, or the same layout to share memory.`,
        );
      }
    }
  }

  const manager = new SandboxMemoryGenerationManager(args);
  registry ??= new Map();
  registry.set(layoutKey, {
    memory: args.memory,
    manager,
    generationSignature: memoryGenerationSignature(args.memory.generate),
    layout,
  });
  managersBySession.set(args.session, registry);
  return manager;
}

export class SandboxMemoryGenerationManager {
  private readonly session: SandboxSessionLike<SandboxSessionState>;
  private readonly memory: Memory;
  private readonly flushMode: MemoryFlushMode;
  private runAs?: string;
  private runAgent: SandboxMemoryAgentRunner;
  private readonly storage: SandboxMemoryStorage;
  private readonly pendingRolloutIds = new Set<string>();
  private flushPromise?: Promise<void>;
  private stopped = false;
  private unregisterPreStopHook?: () => void;

  constructor(args: {
    session: SandboxSessionLike<SandboxSessionState>;
    memory: Memory;
    runAs?: string;
    runAgent: SandboxMemoryAgentRunner;
  }) {
    this.session = args.session;
    this.memory = args.memory;
    this.flushMode = args.memory.generate?.flush ?? 'preStop';
    this.runAs = args.runAs;
    this.runAgent = args.runAgent;
    this.storage = new SandboxMemoryStorage({
      session: args.session,
      runAs: args.runAs,
      store: args.memory.store,
    });
    // enqueueState 只把 run 写成原材料。preStop 模式下，Phase 1/Phase 2 在
    // sandbox pre-stop/cleanup 时 flush，保证任务执行完再整理记忆。
    // manual 模式不注册 hook：run 收尾只 enqueue，提取由调用方通过
    // runMemoryExtraction 在自己的节奏里触发（顺带避免 interruption 保活路径
    // 触发的提前 flush）。
    if (this.flushMode === 'preStop') {
      this.unregisterPreStopHook = registerSandboxPreStopHook(
        this.session,
        async () => {
          await this.flush();
        },
      );
    }
  }

  isStopped(): boolean {
    return this.stopped;
  }

  updateRuntimeContext(args: {
    runAs?: string;
    runAgent: SandboxMemoryAgentRunner;
  }): void {
    this.runAs = args.runAs;
    this.runAgent = args.runAgent;
    this.storage.updateRuntimeContext({ runAs: args.runAs });
  }

  async enqueueState<TContext>(
    state: RunState<TContext, Agent<TContext, AgentOutputType>>,
    args: {
      exception?: unknown;
      inputOverride?: string | AgentInputItem[];
      rolloutIdentity: Omit<MemoryRolloutIdentity, 'fallbackId'>;
    },
  ): Promise<void> {
    if (this.memory.generate === null || this.stopped) return;

    // rolloutId 决定写入哪个 sessions/<id>.jsonl。
    // 它不是长期 memory 的隔离边界，长期边界仍是 workspace + memoriesDir。
    const rolloutId = resolveMemoryRolloutId({
      ...args.rolloutIdentity,
      fallbackId: randomUUID(),
    });
    // payload 是本次 run 的可总结 transcript：输入、工具/模型输出、终止状态等。
    const payload = buildMemoryRolloutPayload(state, {
      rolloutId,
      exception: args.exception,
      inputOverride: args.inputOverride,
    });

    await withSandboxSpan(
      'sandbox.memory.enqueue_rollout',
      {
        rollout_id: rolloutId,
      },
      async () => {
        // 先确保 memories/ 和 sessions/ 布局存在，再追加原始 JSONL。
        // pendingRolloutIds 记录本次 cleanup 时需要被 Phase 1 提炼的 rollout。
        await this.storage.ensureLayout(this.memory);
        await this.storage.appendJsonl(
          joinRelativePaths(
            this.memory.layout.sessionsDir,
            `${rolloutId}.jsonl`,
          ),
          payload,
        );
        // manual 模式不用内存 pending 集合：pending 由文件对比推导。
        if (this.flushMode === 'preStop') {
          this.pendingRolloutIds.add(rolloutId);
        }
      },
    );
  }

  async flush(): Promise<void> {
    if (this.flushPromise) {
      await this.flushPromise;
      return;
    }
    this.flushPromise = this.flushOnce();
    try {
      await this.flushPromise;
    } finally {
      this.flushPromise = undefined;
    }
  }

  private async flushOnce(): Promise<void> {
    if (this.memory.generate === null) {
      return;
    }

    // manual 模式没有一次性 stopped 语义：每次调用都按文件对比重新推导
    // pending 并提取，天然幂等；也不注销自己（manager 可继续 enqueue）。
    if (this.flushMode === 'manual') {
      await runMemoryExtraction({
        session: this.session,
        memory: this.memory,
        runAs: this.runAs,
        runAgent: this.runAgent,
      });
      return;
    }

    if (this.stopped) {
      this.unregister();
      return;
    }
    this.stopped = true;

    try {
      await withSandboxSpan(
        'sandbox.memory.flush',
        {
          rollout_count: this.pendingRolloutIds.size,
        },
        async () => {
          // flush 是 memory 真正形成的地方：
          // 1. 每个 pending rollout 先跑 Phase 1，生成 raw memory 和 rollout summary。
          // 2. 只要 Phase 1 有产物，就跑 Phase 2，把它们合并进长期 memory。
          await this.storage.ensureLayout(this.memory);
          await extractPendingRollouts(
            this.phaseContext(),
            [...this.pendingRolloutIds].sort(),
          );
        },
      );
    } finally {
      this.pendingRolloutIds.clear();
      this.unregister();
    }
  }

  private phaseContext(): MemoryPhaseContext {
    return {
      session: this.session,
      memory: this.memory,
      runAs: this.runAs,
      runAgent: this.runAgent,
      storage: this.storage,
    };
  }

  private unregister(): void {
    this.unregisterPreStopHook?.();
    this.unregisterPreStopHook = undefined;

    const registry = managersBySession.get(this.session);
    if (!registry) {
      return;
    }
    const layoutKey = memoryLayoutKey(this.memory);
    const existing = registry.get(layoutKey);
    if (existing?.manager === this) {
      registry.delete(layoutKey);
    }
    if (registry.size === 0) {
      managersBySession.delete(this.session);
    }
  }
}

type MemoryPhaseContext = {
  session: SandboxSessionLike<SandboxSessionState>;
  memory: Memory;
  runAs?: string;
  runAgent: SandboxMemoryAgentRunner;
  storage: SandboxMemoryStorage;
};

export type MemoryExtractionResult = {
  /** 本次扫描判定为 pending 的 rollout（按 id 排序）。 */
  pendingRolloutIds: string[];
  /** Phase 1 成功产出并落盘的 rollout。 */
  extractedRolloutIds: string[];
  /** Phase 2 consolidation 是否成功跑完。 */
  consolidated: boolean;
};

/**
 * 脱离式提取的公开入口：给一个能访问 memory 布局的沙盒 session，
 * 自己扫 pending（文件对比推导）→ 逐个跑 Phase 1 → 有产物则跑 Phase 2。
 *
 * 与 pre-stop flush 不同，它不依赖任何内存状态，可以在任意进程/沙盒里对同
 * 一份 memory 目录反复调用：漏提的 rollout 下次扫描自动补，失败自动重试。
 * 调用方负责并发控制（同一 memory 目录同一时刻只应有一个提取在跑）。
 */
export async function runMemoryExtraction(args: {
  session: SandboxSessionLike<SandboxSessionState>;
  memory: Memory;
  runAgent: SandboxMemoryAgentRunner;
  runAs?: string;
}): Promise<MemoryExtractionResult> {
  if (args.memory.generate === null) {
    throw new UserError(
      'runMemoryExtraction requires Memory.generate to be enabled.',
    );
  }
  const ctx: MemoryPhaseContext = {
    session: args.session,
    memory: args.memory,
    runAs: args.runAs,
    runAgent: args.runAgent,
    storage: new SandboxMemoryStorage({
      session: args.session,
      runAs: args.runAs,
      store: args.memory.store,
    }),
  };
  return await withSandboxSpan('sandbox.memory.extract', {}, async () => {
    await ctx.storage.ensureLayout(ctx.memory);
    const pendingRolloutIds = await scanPendingRolloutIds(
      ctx.storage,
      ctx.memory,
    );
    const { extractedRolloutIds, consolidated } = await extractPendingRollouts(
      ctx,
      pendingRolloutIds,
      // pending 是无状态推导出来的，no-op 必须落 marker 才能收敛；
      // preStop 路径的 pending 是一次性内存集合，不需要（也不该留 marker 噪音）。
      { markNoOpExtractions: true },
    );
    if (extractedRolloutIds.length > 0) {
      await sanitizeWorkspaceAbsolutePaths(ctx);
    }
    return { pendingRolloutIds, extractedRolloutIds, consolidated };
  });
}

/**
 * 文件对比推导 pending：`sessions/<id>.jsonl` 存在但 `raw_memories/<id>.md`
 * 缺失，或 JSONL 末条 `updated_at` 晚于 raw memory 头部记录的 `updated_at`。
 * 无额外状态，天然幂等、崩溃自愈。
 */
export async function scanPendingRolloutIds(
  storage: SandboxMemoryStorage,
  memory: Memory,
): Promise<string[]> {
  const sessionsDir = memory.layout.sessionsDir;
  const entries = await storage.listDir(sessionsDir);
  const pending: string[] = [];
  for (const entry of entries) {
    if (entry.type === 'dir' || !entry.name.endsWith('.jsonl')) {
      continue;
    }
    const rolloutId = entry.name.slice(0, -'.jsonl'.length);
    try {
      validateRolloutId(rolloutId);
    } catch {
      continue;
    }
    // 末行不完整（可能正被并发追加）或文件为空：这一轮跳过，下轮自愈。
    const tail = await storage.readJsonlTailRecord(
      joinRelativePaths(sessionsDir, entry.name),
    );
    if (tail === null) {
      continue;
    }
    const rawMemory = await storage.readText(
      joinRelativePaths(
        memory.layout.memoriesDir,
        'raw_memories',
        `${rolloutId}.md`,
      ),
    );
    if (rawMemory === null) {
      pending.push(rolloutId);
      continue;
    }
    // rolloutId 通常是 conversationId，同一对话多次 run 追加同一 JSONL：
    // transcript 比已提取的 raw memory 新 ⇒ 需要重提。
    const tailUpdatedAt =
      typeof tail.updated_at === 'string' ? tail.updated_at : '';
    const extractedUpdatedAt = extractMemoryMetadataValue(
      rawMemory,
      'updated_at',
    );
    if (
      tailUpdatedAt &&
      (!extractedUpdatedAt ||
        extractedUpdatedAt === 'unknown' ||
        tailUpdatedAt > extractedUpdatedAt)
    ) {
      pending.push(rolloutId);
    }
  }
  return pending.sort();
}

async function extractPendingRollouts(
  ctx: MemoryPhaseContext,
  pendingRolloutIds: string[],
  options: { markNoOpExtractions?: boolean } = {},
): Promise<{ extractedRolloutIds: string[]; consolidated: boolean }> {
  const extractedRolloutIds: string[] = [];
  for (const rolloutId of pendingRolloutIds) {
    try {
      const extraction = await runPhaseOne(ctx, rolloutId);
      if (!validateRolloutArtifacts(extraction)) {
        // 文件对比推导 pending 的路径下，「无可留记忆」也要落一个 no-op
        // marker（仅元数据、空正文），否则该 rollout 永远 pending，每轮 pass
        // 都会白跑一次 Phase 1。marker 不带 rollout_summary_file，天然被
        // Phase 2 的选择逻辑排除。
        if (options.markNoOpExtractions) {
          await persistNoOpExtractionMarker(ctx, rolloutId);
        }
        continue;
      }
      await persistPhaseOneArtifacts(ctx, rolloutId, extraction);
      extractedRolloutIds.push(rolloutId);
    } catch (error) {
      logger.warn(
        `Sandbox memory phase 1 failed for rollout ${rolloutId}: ${error}`,
      );
    }
  }

  let consolidated = false;
  if (extractedRolloutIds.length > 0) {
    try {
      await runPhaseTwo(ctx);
      consolidated = true;
    } catch (error) {
      logger.warn(`Sandbox memory phase 2 failed: ${error}`);
    }
  }
  return { extractedRolloutIds, consolidated };
}

async function runPhaseOne(
  ctx: MemoryPhaseContext,
  rolloutId: string,
): Promise<RolloutExtractionArtifacts> {
  const generate = ctx.memory.generate;
  if (generate === null) {
    throw new UserError('Memory generation is disabled.');
  }

  // Phase 1 的输入是 sessions/<rollout-id>.jsonl，也就是 run 收尾时写下的原始轨迹。
  const transcriptPath = joinRelativePaths(
    ctx.memory.layout.sessionsDir,
    `${rolloutId}.jsonl`,
  );
  const transcript = await ctx.storage.readText(transcriptPath);
  if (!transcript) {
    throw new UserError(
      `Memory rollout transcript was not found: ${transcriptPath}`,
    );
  }

  // Phase 1 内部再启动一个 SandboxAgent，让模型把单个 rollout 压缩成三件东西：
  // raw_memory、rollout_summary、rollout_slug。
  const { SandboxAgent } = await import('../agent');
  const phaseOneAgent = new SandboxAgent({
    name: 'sandbox-memory-phase-one',
    instructions: renderRolloutExtractionInstructions(generate.extraPrompt),
    model: generate.phaseOneModel,
    modelSettings: generate.phaseOneModelSettings,
    outputType: RolloutExtractionArtifactsSchema,
    defaultManifest: currentManifest(ctx),
    runAs: ctx.runAs,
  });
  const result = await ctx.runAgent(
    phaseOneAgent,
    renderRolloutExtractionUserPrompt({
      terminalMetadataJson: renderPhaseOnePrompt(transcript),
      rolloutContents: transcript,
    }),
    {
      sandbox: {
        session: ctx.session,
        manifest: currentManifest(ctx),
      },
      maxTurns: 500,
    },
  );

  return RolloutExtractionArtifactsSchema.parse(result.finalOutput);
}

async function persistPhaseOneArtifacts(
  ctx: MemoryPhaseContext,
  rolloutId: string,
  extraction: RolloutExtractionArtifacts,
): Promise<void> {
  const memoriesDir = ctx.memory.layout.memoriesDir;
  const transcriptPath = joinRelativePaths(
    ctx.memory.layout.sessionsDir,
    `${rolloutId}.jsonl`,
  );
  const transcript = await ctx.storage.readText(transcriptPath);
  const metadata = rolloutMetadataFromTranscript(transcript ?? '');
  const slug = normalizeRolloutSlug(extraction.rollout_slug);
  const rolloutPath = joinRelativePaths(
    ctx.memory.layout.sessionsDir,
    `${rolloutId}.jsonl`,
  );
  const rolloutSummaryFile = joinRelativePaths(
    'rollout_summaries',
    `${rolloutId}_${slug}.md`,
  );
  // raw_memories 是 Phase 2 的主要输入；rollout_summaries 是更可读的证据摘要。
  await ctx.storage.writeText(
    joinRelativePaths(memoriesDir, 'raw_memories', `${rolloutId}.md`),
    formatRawMemory({
      updatedAt: metadata.updatedAt,
      rolloutId,
      rolloutPath,
      rolloutSummaryFile,
      terminalState: metadata.terminalState,
      rawMemory: extraction.raw_memory,
    }),
  );
  await ctx.storage.writeText(
    joinRelativePaths(memoriesDir, rolloutSummaryFile),
    formatRolloutSummary({
      updatedAt: metadata.updatedAt,
      rolloutPath,
      sessionId: String(ctx.session.state.sessionId ?? ''),
      terminalState: metadata.terminalState,
      rolloutSummary: extraction.rollout_summary,
    }),
  );
}

/**
 * 兜底清洗：把 memory 产物里泄漏的「当前沙盒绝对根路径」改写回工作区相对路径。
 * 脱离式提取跑在一次性 worker 沙盒里，绝对根路径 pass 结束即失效，而记忆会在
 * 根路径不同的其他工作区中被读取。prompt 已要求 phase agent 只写相对路径，
 * 这里对漏网的做确定性改写（只处理指向本次工作区内部的前缀，其余文本不动）。
 */
async function sanitizeWorkspaceAbsolutePaths(
  ctx: MemoryPhaseContext,
): Promise<void> {
  const workspaceRootPath = (
    ctx.session.state as { workspaceRootPath?: unknown }
  ).workspaceRootPath;
  if (typeof workspaceRootPath !== 'string' || workspaceRootPath.length < 2) {
    return;
  }
  const prefix = workspaceRootPath.endsWith('/')
    ? workspaceRootPath
    : `${workspaceRootPath}/`;
  const files = await listMemoryFilesRecursively(
    ctx.storage,
    ctx.memory.layout.memoriesDir,
    4,
  );
  for (const path of files) {
    try {
      const content = await ctx.storage.readText(path);
      if (content === null || !content.includes(prefix)) {
        continue;
      }
      await ctx.storage.writeText(path, content.split(prefix).join(''));
    } catch (error) {
      logger.warn(
        `Failed to sanitize absolute workspace paths in memory file ${path}: ${error}`,
      );
    }
  }
}

async function listMemoryFilesRecursively(
  storage: SandboxMemoryStorage,
  dir: string,
  maxDepth: number,
): Promise<string[]> {
  if (maxDepth <= 0) {
    return [];
  }
  const files: string[] = [];
  for (const entry of await storage.listDir(dir)) {
    const childPath = joinRelativePaths(dir, entry.name);
    if (entry.type === 'dir') {
      files.push(
        ...(await listMemoryFilesRecursively(storage, childPath, maxDepth - 1)),
      );
    } else {
      files.push(childPath);
    }
  }
  return files;
}

async function persistNoOpExtractionMarker(
  ctx: MemoryPhaseContext,
  rolloutId: string,
): Promise<void> {
  const rolloutPath = joinRelativePaths(
    ctx.memory.layout.sessionsDir,
    `${rolloutId}.jsonl`,
  );
  const transcript = await ctx.storage.readText(rolloutPath);
  const metadata = rolloutMetadataFromTranscript(transcript ?? '');
  await ctx.storage.writeText(
    joinRelativePaths(
      ctx.memory.layout.memoriesDir,
      'raw_memories',
      `${rolloutId}.md`,
    ),
    [
      `rollout_id: ${rolloutId}`,
      `updated_at: ${metadata.updatedAt}`,
      `rollout_path: ${rolloutPath}`,
      `terminal_state: ${metadata.terminalState}`,
      '',
    ].join('\n'),
  );
}

async function runPhaseTwo(ctx: MemoryPhaseContext): Promise<void> {
  const generate = ctx.memory.generate;
  if (generate === null) {
    throw new UserError('Memory generation is disabled.');
  }

  // Phase 2 会选择一批 raw memories 作为输入，避免无限增长。
  // 选择结果也会记录下来，后续增量整理可知道哪些 rollout 被保留或移除。
  const selection = await ctx.storage.buildPhaseTwoInputSelection({
    memoriesDir: ctx.memory.layout.memoriesDir,
    maxRawMemoriesForConsolidation:
      generate.maxRawMemoriesForConsolidation ?? 256,
  });
  if (
    !(await ctx.storage.rebuildRawMemories({
      memoriesDir: ctx.memory.layout.memoriesDir,
      selectedItems: selection.selected,
    }))
  ) {
    return;
  }

  // Phase 2 内部再启动一个 SandboxAgent，按 consolidation prompt 更新
  // memories/MEMORY.md、memories/memory_summary.md，以及必要的 skills/ 内容。
  const { SandboxAgent } = await import('../agent');
  const phaseTwoAgent = new SandboxAgent({
    name: 'sandbox-memory-phase-two',
    model: generate.phaseTwoModel,
    modelSettings: generate.phaseTwoModelSettings,
    defaultManifest: currentManifest(ctx),
    runAs: ctx.runAs,
  });
  await ctx.runAgent(
    phaseTwoAgent,
    renderMemoryConsolidationPrompt({
      memoryRoot: ctx.memory.layout.memoriesDir,
      selection,
      extraPrompt: generate.extraPrompt,
    }),
    {
      sandbox: {
        session: ctx.session,
        manifest: currentManifest(ctx),
      },
      maxTurns: 500,
    },
  );
  await ctx.storage.writePhaseTwoSelection({
    memoriesDir: ctx.memory.layout.memoriesDir,
    selectedItems: selection.selected,
  });
}

function currentManifest(ctx: MemoryPhaseContext): Manifest {
  return new Manifest(ctx.session.state.manifest);
}

function normalizedLayout(
  memory: Memory,
): MemoryManagerRegistryEntry['layout'] {
  return {
    memoriesDir: normalizeLayoutPath(memory.layout.memoriesDir),
    sessionsDir: normalizeLayoutPath(memory.layout.sessionsDir),
    summaryFile: normalizeLayoutPath(memory.layout.summaryFile),
  };
}

function memoryLayoutKey(memory: Memory): string {
  const layout = normalizedLayout(memory);
  return `${layout.memoriesDir}\0${layout.sessionsDir}\0${layout.summaryFile}`;
}

function memoryGenerationSignature(generate: MemoryGenerateConfig): string {
  return stableJsonStringify(generate, {
    encodeFunction: (value) => `function:${value.name || 'anonymous'}`,
    encodeNonPlainObject: (value) => `object:${objectId(value)}`,
  });
}

function validateRolloutArtifacts(
  artifacts: RolloutExtractionArtifacts,
): boolean {
  const hasSlug = artifacts.rollout_slug.trim().length > 0;
  const hasSummary = artifacts.rollout_summary.trim().length > 0;
  const hasRawMemory = artifacts.raw_memory.trim().length > 0;
  if (!hasSlug && !hasSummary && !hasRawMemory) {
    return false;
  }
  if (!hasSlug || !hasSummary || !hasRawMemory) {
    throw new UserError('Phase 1 returned partially-empty memory artifacts.');
  }
  normalizeRolloutSlug(artifacts.rollout_slug);
  return true;
}

function rolloutMetadataFromTranscript(transcript: string): {
  updatedAt: string;
  terminalState: string;
} {
  const payloads = transcript
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .map((line) => JSON.parse(line)) as Array<Record<string, unknown>>;
  const lastPayload = payloads[payloads.length - 1];
  if (!lastPayload) {
    return {
      updatedAt: 'unknown',
      terminalState: 'unknown',
    };
  }
  const terminalMetadata = lastPayload.terminal_metadata;
  const terminalState =
    terminalMetadata && typeof terminalMetadata === 'object'
      ? String(
          (terminalMetadata as Record<string, unknown>).terminal_state ??
            'unknown',
        )
      : 'unknown';
  return {
    updatedAt:
      typeof lastPayload.updated_at === 'string'
        ? lastPayload.updated_at
        : 'unknown',
    terminalState,
  };
}

function formatRawMemory(args: {
  updatedAt: string;
  rolloutId: string;
  rolloutPath: string;
  rolloutSummaryFile: string;
  terminalState: string;
  rawMemory: string;
}): string {
  return [
    `rollout_id: ${args.rolloutId}`,
    `updated_at: ${args.updatedAt}`,
    `rollout_path: ${args.rolloutPath}`,
    `rollout_summary_file: ${args.rolloutSummaryFile}`,
    `terminal_state: ${args.terminalState}`,
    '',
    args.rawMemory.trimEnd(),
    '',
  ].join('\n');
}

function formatRolloutSummary(args: {
  updatedAt: string;
  rolloutPath: string;
  sessionId: string;
  terminalState: string;
  rolloutSummary: string;
}): string {
  return [
    `session_id: ${args.sessionId}`,
    `updated_at: ${args.updatedAt}`,
    `rollout_path: ${args.rolloutPath}`,
    `terminal_state: ${args.terminalState}`,
    '',
    args.rolloutSummary.trimEnd(),
    '',
  ].join('\n');
}

function normalizeLayoutPath(value: string): string {
  return value
    .split('/')
    .filter((part) => part.length > 0 && part !== '.')
    .join('/');
}

function objectId(value: object): number {
  const existing = objectIds.get(value);
  if (existing) {
    return existing;
  }
  const id = nextObjectId++;
  objectIds.set(value, id);
  return id;
}
